import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Save, FileText, GitCompareArrows, RotateCcw, Search,
  MapPin, Building2, BadgeCheck, AlertTriangle, Loader2, Coins,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { DEFAULT_PRICING, computeDossier, clientRange, precariteRange, pctToPrice, pctNum, fmt, euros, zoneFromPostcode, CLIENTS } from "@/lib/cee";
import { PrixForces } from "@/components/PrixForces";
import { AnimatedNumber, FadeIn } from "@/components/Reveal";
import { OperationCard } from "@/components/OperationCard";
import DevisDocument from "@/components/DevisDocument";

const uid = () => Math.random().toString(36).slice(2, 10);
const newOp = () => ({
  id: uid(),
  fiche: "BAR-TH-137",
  zone: "H1",
  typeSysteme: "B",
  caisson: "basse_conso",
  usage: "chauffage",
  application: "moyenne_haute",
  nbLogements: "",
  etas: "",
  pacs: [""],
  puissanceChaufferie: "",
});

const INIT_FORM = {
  adresseTravaux: "",
  benefSiren: "",
  clientNom: "",
  benefAdresse: "",
  devisNum: "",
  devisValidite: "30",
  entTel: "",
  entEmail: "",
  commentaire: "",
  prixForces: {},
  coutDevis: "",
  oblige: "",
  commissionPct: 100,
  commissionPctPrecarite: 100,
  departement: "",
  versementPrime: "remise_sur_devis",
  statut: "En création",
};

/* ---------- Adresse avec autocomplétion Base Adresse Nationale ---------- */
const ReseauInfo = ({ r }) => {
  if (!r) return null;
  if (r.loading) return <p data-testid="calc-reseau-result" className="text-[12px] text-[var(--mute)]">Vérification de la proximité d'un réseau de chaleur…</p>;
  if (r.error) return <p data-testid="calc-reseau-result" className="text-[12px] text-[var(--amber-text)]">Service France Chaleur Urbaine indisponible : vérifiez la proximité d'un réseau manuellement.</p>;
  return (
    <p data-testid="calc-reseau-result" className={`text-[12px] ${r.isEligible ? "text-[var(--amber-text)]" : "text-[var(--green-text)]"}`}>
      {r.isEligible
        ? `Réseau de chaleur à proximité : « ${r.name || "réseau"} »${r.gestionnaire ? ` (${r.gestionnaire})` : ""} à ${r.distance ?? "?"} m — raccordement envisageable.`
        : `Aucun réseau de chaleur à proximité${r.distance ? ` (le plus proche à ${r.distance} m)` : ""}.`}{" "}
      <span className="text-[var(--mute)]">Source : France Chaleur Urbaine (ADEME / data.gouv.fr).</span>
    </p>
  );
};

const AddrInput = ({ value, onChange, onPick }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const ctrl = useRef(null);

  const onInput = (v) => {
    onChange(v);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < 4) {
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      if (ctrl.current) ctrl.current.abort();
      ctrl.current = new AbortController();
      setLoading(true);
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?limit=5&q=${encodeURIComponent(v)}`,
          { signal: ctrl.current.signal }
        );
        const data = await res.json();
        setSuggestions(data.features || []);
        setOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 260);
  };

  return (
    <div className="relative">
      <input
        data-testid="calc-adresse-input"
        className="field-input pr-9"
        placeholder="Ex. 12 rue de l'Industrie, 69007 Lyon"
        value={value}
        onChange={(e) => onInput(e.target.value)}
      />
      {loading && <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[var(--accent-text)]" />}
      {open && (
        <div className="glass absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-[var(--line-hover)] shadow-2xl">
          {suggestions.length === 0 && <div className="px-4 py-3 text-xs text-[var(--mute)]">Aucune adresse trouvée.</div>}
          {suggestions.map((f, i) => {
            const p = f.properties || {};
            return (
              <button
                type="button"
                key={i}
                data-testid={`adresse-suggestion-${i}`}
                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-[13px] text-[var(--ink)] hover:bg-sky-500/10"
                onClick={() => {
                  onChange(p.label || "");
                  onPick(p.postcode, f.geometry?.coordinates);
                  setOpen(false);
                }}
              >
                <MapPin size={13} className="mt-0.5 shrink-0 text-[var(--accent-text)]" />
                <span>
                  {p.name || p.label}
                  <span className="ml-2 text-[var(--mute)]">{[p.postcode, p.city].filter(Boolean).join(" ")}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ---------- Page calculateur ---------- */
const Calculateur = () => {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [form, setForm] = useState(INIT_FORM);
  const [ops, setOps] = useState([newOp()]);
  const [zoneInfo, setZoneInfo] = useState(null);

  const checkReseau = async (coords) => {
    if (!coords) return;
    const [lon, lat] = coords;
    setF({ reseauChaleur: { loading: true } });
    try {
      const { data } = await api.get("/reseau-chaleur", { params: { lat, lon } });
      setF({ reseauChaleur: data });
      if (!data.isEligible) setOps((list) => list.map((o) => (o.fiche === "BAR-TH-179" ? { ...o, cdpRaccordementImpossible: true } : o)));
    } catch (e) {
      setF({ reseauChaleur: { error: true } });
    }
  };
  const onPickAddress = (cp, coords) => {
    applyZone(cp);
    checkReseau(coords);
  };

  const applyZone = (postcode) => {
    const found = zoneFromPostcode(postcode);
    if (!found) {
      setZoneInfo(postcode ? { error: true, cp: postcode } : null);
      return;
    }
    setZoneInfo({ ...found, cp: postcode });
    setF({ departement: found.dept });
    setOps((list) => list.map((o) => ({ ...o, zone: found.zone })));
  };
  const addrPostcode = (v) => (String(v || "").match(/\b(\d{5})\b/) || [])[1];
  const [saving, setSaving] = useState(false);
  const [devis, setDevis] = useState(null);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setOp = (id, patch) => setOps((list) => list.map((o) => (o.id === id ? { ...o, ...patch } : o)));

  useEffect(() => {
    api.get("/pricing").then((d) => setPricing(d.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId) return;
    api
      .get(`/dossiers/${editId}`)
      .then(({ data }) => {
        setForm({
          ...INIT_FORM,
          ...data,
          prixForces: data.prixForces || {},
          coutDevis: data.coutDevis ? String(data.coutDevis) : "",
          oblige: data.oblige || "",
          commissionPct: data.commissionPct ?? 100,
          commissionPctPrecarite: data.commissionPctPrecarite ?? 100,
          departement: data.departement || "",
        });
        setOps((data.operations || []).map((o) => ({ ...newOp(), ...o })));
        if (!data.operations?.length) setOps([newOp()]);
        setComputed({ sig: null, totals: data.totals, operationResults: data.operationResults });
        const cp = (String(data.adresseTravaux || "").match(/\b(\d{5})\b/) || [])[1];
        const found = zoneFromPostcode(cp);
        if (found) setZoneInfo({ ...found, cp });
      })
      .catch((e) => toast.error(apiErrorMessage(e)));
  }, [editId]);

  const range = clientRange(pricing);
  const rangePrec = precariteRange(pricing);
  const cession = pctToPrice(form.commissionPct, range);
  const cessionPrec = pctToPrice(form.commissionPctPrecarite, rangePrec);
  const local = useMemo(
    () => computeDossier(ops, { ...form, prixCession: cession, prixCessionPrecarite: cessionPrec }, pricing),
    [ops, form, pricing, cession, cessionPrec]
  );
  // Montants (prime, commission) calculés côté serveur : les prix ne sont jamais envoyés au partenaire
  const [server, setServer] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      api
        .post("/compute", {
          operations: ops.map(({ id, ...rest }) => rest),
          departement: form.departement || "",
          commissionPct: pctNum(form.commissionPct),
          commissionPctPrecarite: pctNum(form.commissionPctPrecarite),
          oblige: form.oblige || "",
          prixForces: isAdmin ? form.prixForces || {} : null,
          coutDevis: isAdmin ? parseFloat(form.coutDevis) || 0 : null,
        })
        .then(({ data }) => setServer(data))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [ops, form.departement, form.commissionPct, form.oblige, form.prixForces, form.coutDevis, isAdmin]);
  // Partenaire : la prime n'est affichée qu'après « Calculer la prime », tant que la saisie n'a pas changé
  const sig = JSON.stringify([ops.map(({ id, ...r }) => r), form.departement, form.commissionPct]);
  const [computed, setComputed] = useState(null);
  useEffect(() => {
    if (computed && computed.sig === null) setComputed((c) => ({ ...c, sig }));
  }, [computed, sig]);
  const primeShown = isAdmin || (computed && computed.sig === sig);
  const results = local.results.map((r, i) => ({
    ...r,
    prime: isAdmin ? server?.operationResults?.[i]?.prime ?? r.prime : primeShown ? computed.operationResults?.[i]?.prime ?? null : null,
  }));
  const totals = isAdmin
    ? { ...local.totals, ...(server?.totals || {}) }
    : { ...local.totals, ...(server?.totals || {}), ...(primeShown ? computed.totals : { primeNette: 0, primeClassique: 0, primePrecarite: 0, commissionTotal: 0 }) };

  const buildPayload = () => ({
    nom: form.clientNom || "",
    statut: form.statut || "En création",
    adresseTravaux: form.adresseTravaux,
    benefSiren: form.benefSiren,
    clientNom: form.clientNom,
    benefAdresse: form.benefAdresse,
    devisNum: form.devisNum,
    devisValidite: form.devisValidite,
    entTel: form.entTel,
    entEmail: form.entEmail,
    commentaire: form.commentaire || "",
    prixForces: isAdmin ? form.prixForces || {} : null,
    coutDevis: isAdmin ? parseFloat(form.coutDevis) || 0 : null,
    commissionPctPrecarite: pctNum(form.commissionPctPrecarite),
    departement: form.departement || "",
    reseauChaleur: form.reseauChaleur?.distance !== undefined || form.reseauChaleur?.isEligible !== undefined ? form.reseauChaleur : null,
    versementPrime: "remise_sur_devis",
    operations: ops.map(({ id, ...rest }) => rest),
  });

  const saveDossier = async () => {
    if (!CLIENTS.includes(form.clientNom)) {
      toast.error("Choisissez le client (RIVP ou ELOGIE SIEMP) avant d'enregistrer.");
      return null;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const { data } = editId
        ? await api.put(`/dossiers/${editId}`, payload)
        : await api.post("/dossiers", payload);
      setComputed({ sig, totals: data.totals, operationResults: data.operationResults });
      toast.success(isAdmin ? `Dossier « ${data.nom} » ${editId ? "mis à jour" : "enregistré"}.` : `Prime calculée — dossier « ${data.nom} » enregistré.`, { description: `${fmt(data.totals.mwhCumacTotal, 1)} MWh cumac · ${euros(data.totals.primeNette)}` });
      if (!editId) setSearchParams({ edit: data.id }, { replace: true });
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const generateDevis = async () => {
    let num = form.devisNum.trim();
    if (!num) {
      const { data: all } = await api.get("/dossiers");
      num = `EST-${new Date().getFullYear()}-${String(all.length + 1).padStart(3, "0")}`;
      setF({ devisNum: num });
    }
    const saved = await saveDossier();
    if (!saved) return;
    setDevis({ ...saved, devisNum: num });
  };

  const addSimulation = async () => {
    try {
      const payload = buildPayload();
      const { data } = await api.post("/simulations", {
        nom: form.clientNom || `Simulation ${new Date().toLocaleDateString("fr-FR")}`,
        oblige: form.oblige || "",
        prixForces: payload.prixForces,
        coutDevis: payload.coutDevis,
        commissionPct: payload.commissionPct,
        commissionPctPrecarite: payload.commissionPctPrecarite,
        departement: payload.departement,
        operations: payload.operations,
      });
      toast.success("Simulation ajoutée au comparateur.");
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const resetAll = () => {
    setForm(INIT_FORM);
    setOps([newOp()]);
    setZoneInfo(null);
    setSearchParams({}, { replace: true });
    toast.info("Nouveau dossier prêt.");
  };


  return (
    <div className="mx-auto max-w-[1440px] px-4 pb-24 sm:px-6">
      {/* En-tête */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-[11px] font-mono uppercase tracking-[0.24em] text-[var(--accent-text)]">Calculateur de prime CEE</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--ink)] sm:text-4xl">
            {editId ? "Modifier le dossier" : "Nouveau dossier de prime"}
          </h1>
        </div>
        {editId && (
          <button data-testid="new-dossier-btn" onClick={resetAll} className="btn-ghost">
            <RotateCcw size={14} /> Nouveau dossier
          </button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* ------- Colonne formulaire ------- */}
        <div className="space-y-6">
          {/* Client */}
          <FadeIn className="card-surface p-6">
            <h2 className="font-display mb-1 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
              <BadgeCheck size={17} className="text-[var(--accent-text)]" /> Client
            </h2>
            <p className="mb-4 text-[13px] text-[var(--mute)]">Choix obligatoire avant l'enregistrement du dossier.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {CLIENTS.map((c) => (
                <button
                  type="button"
                  key={c}
                  data-testid={`calc-client-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setF({ clientNom: c })}
                  className={`rounded-xl border p-4 text-left text-sm font-semibold transition-all ${
                    form.clientNom === c
                      ? "border-sky-500/60 bg-sky-500/10 text-[var(--accent-text)]"
                      : "border-[var(--line)] bg-[var(--inset)] text-[var(--ink)] hover:border-[var(--line-hover)]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </FadeIn>

          {/* Dossier */}
          <FadeIn className="card-surface p-6">
            <h2 className="font-display mb-5 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
              <Building2 size={17} className="text-[var(--accent-text)]" /> Dossier
            </h2>
            <div className="space-y-3">
              <div onBlur={() => { const cp = addrPostcode(form.adresseTravaux); if (cp && cp !== zoneInfo?.cp) applyZone(cp); }}>
                <AddrInput value={form.adresseTravaux} onChange={(v) => { setF({ adresseTravaux: v }); if (!v.trim()) setZoneInfo(null); }} onPick={onPickAddress} />
              </div>
              <ReseauInfo r={form.reseauChaleur} />
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Département des travaux</label>
                <input data-testid="calc-departement-input" className="field-input w-24 font-mono" maxLength={3} placeholder="Ex. 69"
                  value={form.departement} onChange={(e) => setF({ departement: e.target.value.toUpperCase() })} />
                <span data-testid="calc-precarite-pct" className="text-[12px] text-[var(--dim)]">
                  {isAdmin && totals.departement && totals.pctPrecarite > 0 ? (
                    <>Part précarité : <b className="text-[var(--accent-text)]">{fmt(totals.pctPrecarite * 100)} %</b> du volume (colonne {totals.colonnePrecarite} de l'annexe I)</>
                  ) : (
                    "Rempli automatiquement depuis l'adresse."
                  )}
                </span>
              </div>
              {zoneInfo && !zoneInfo.error ? (
                <p data-testid="calc-zone-detected" className="text-[12px] text-[var(--accent-text)]">
                  Zone climatique détectée : <b>{zoneInfo.zone}</b> (département {zoneInfo.dept}) — appliquée à toutes les opérations et verrouillée (déterminée par l'adresse des travaux).
                </p>
              ) : zoneInfo?.error ? (
                <p data-testid="calc-zone-unknown" className="text-[12px] text-[var(--amber-text)]">
                  Zone climatique non déterminée pour ce code postal (hors France métropolitaine) — sélectionnez-la sur chaque opération.
                </p>
              ) : (
                <p className="text-[11px] text-[var(--mute)]">
                  Choisissez une adresse dans la liste : la zone climatique H1 / H2 / H3 sera remplie automatiquement sur les opérations.
                </p>
              )}
            </div>
          </FadeIn>

          {/* Opérations */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-[var(--ink)]">
                Opérations du dossier <span className="ml-2 rounded-full bg-sky-500/12 px-2.5 py-0.5 font-mono text-xs text-[var(--accent-text)]">{ops.length}</span>
              </h2>
              <button
                data-testid="add-operation-btn"
                onClick={() => setOps((l) => [...l, { ...newOp(), zone: zoneInfo?.zone || l[l.length - 1]?.zone || "H1" }])}
                className="btn-ghost"
              >
                <Plus size={14} /> Ajouter une opération
              </button>
            </div>
            <p className="mb-4 text-[13px] text-[var(--mute)]">
              Un même dossier peut regrouper plusieurs opérations (raccordement réseau de chaleur, PAC air/eau, VMC).
            </p>
            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {ops.map((op, idx) => (
                  <OperationCard
                    key={op.id}
                    op={op}
                    idx={idx}
                    total={ops.length}
                    result={results[idx]}
                    showSplit={isAdmin}
                    reseau={form.reseauChaleur}
                    zoneLocked={!!zoneInfo && !zoneInfo.error}
                    onChange={(patch) => setOp(op.id, patch)}
                    onDuplicate={() => setOps((l) => [...l.slice(0, idx + 1), { ...op, id: uid() }, ...l.slice(idx + 1)])}
                    onDelete={() => setOps((l) => (l.length > 1 ? l.filter((o) => o.id !== op.id) : l))}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* ------- Rail résultats (sticky) ------- */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="card-surface glow-brand overflow-hidden">
            <div className="border-b border-[var(--line)] bg-gradient-to-br from-sky-500/12 to-transparent p-6">
              <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-[var(--accent-text)]">Prime totale</p>
              <div data-testid="calc-prime-nette" className="font-display mt-2 text-5xl font-extrabold tracking-tight text-[var(--ink)]">
                {primeShown ? <AnimatedNumber value={Math.round(totals.primeNette)} suffix=" €" /> : "— €"}
              </div>
              {!primeShown && (
                <p data-testid="calc-prime-hint" className="mt-2 text-[12px] leading-relaxed text-[var(--mute)]">
                  Cliquez sur « Calculer la prime » pour obtenir le montant. Le dossier sera enregistré.
                </p>
              )}
              {isAdmin && (
              <div data-testid="calc-prime-sub" className="mt-4 space-y-1.5 rounded-xl border border-[var(--line)] bg-[var(--inset)] p-3 font-mono text-[11px] text-[var(--dim)]">
                <div className="flex justify-between gap-2" data-testid="calc-split-classique">
                  <span>Volume classique {fmt((1 - (totals.pctPrecarite || 0)) * 100)} % : {fmt(totals.mwhClassique, 1)} MWh{isAdmin ? ` × ${fmt(totals.prixCession, 2)} €` : ""}</span>
                  <b className="text-[var(--ink)]">{euros(Math.round(totals.primeClassique))}</b>
                </div>
                <div className="flex justify-between gap-2" data-testid="calc-split-precarite">
                  <span>Volume précarité {fmt((totals.pctPrecarite || 0) * 100)} % : {fmt(totals.mwhPrecarite, 1)} MWh{isAdmin ? ` × ${fmt(totals.prixCessionPrecarite, 2)} €` : ""}</span>
                  <b className="text-[var(--ink)]">{euros(Math.round(totals.primePrecarite))}</b>
                </div>
                <div className="flex justify-between gap-2 border-t border-[var(--line)] pt-1.5">
                  <span>Total (classique + précarité){totals.departement ? ` · dép. ${totals.departement}` : " · département à renseigner"}</span>
                  <b className="text-[var(--accent-text)]">{euros(Math.round(totals.primeNette))}</b>
                </div>
              </div>
              )}
            </div>

            <div className="grid grid-cols-3 divide-x divide-[var(--line)] border-b border-[var(--line)] text-center">
              {[
                ["Opérations", fmt(totals.nbOperations), "calc-out-nbop"],
                ["Logements", fmt(totals.logementsTotal), "calc-out-logements"],
                ["kWh cumac", fmt(Math.round(totals.kwhCumacTotal)), "calc-out-kwh"],
              ].map(([l, v, tid]) => (
                <div key={l} className="px-3 py-4">
                  <div data-testid={tid} className="font-display text-lg font-bold text-[var(--ink)]">{v}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-[var(--mute)]">{l}</div>
                </div>
              ))}
            </div>

            <div className="space-y-5 p-6">
              {/* Commission = prime totale − coût du devis (saisi par l'admin) */}
              <div data-testid="calc-commission-block">
                {isAdmin && (
                  <label className="block">
                    <span className="mb-1.5 block text-[11px] font-mono uppercase tracking-widest text-[var(--dim)]">Coût devis (€)</span>
                    <input
                      data-testid="admin-cout-devis-input"
                      type="number" min="0" step="1"
                      className="field-input font-mono"
                      placeholder="Montant du devis des travaux"
                      value={form.coutDevis}
                      onChange={(e) => setF({ coutDevis: e.target.value })}
                    />
                    <span className="mt-1 block text-[10px] font-mono text-[var(--mute)]">
                      Prix appliqué : max pondéré {fmt(totals.prixMaxPondere, 2)} €/MWh
                    </span>
                  </label>
                )}
                <p className="mt-2 text-[11px] leading-relaxed text-[var(--mute)]">
                  Commission d'apport d'affaires (prime totale − coût devis) :{" "}
                  <b data-testid="calc-commission-value" className="text-[var(--accent-text)]">
                    {!primeShown ? "—" : totals.coutDevis > 0 ? euros(Math.round(totals.commissionTotal)) : "coût devis à renseigner"}
                  </b>
                </p>
              </div>

              {/* Panneau interne — visible uniquement de l'administrateur */}
              {isAdmin && (
                <div data-testid="admin-internal-panel" className="space-y-3 rounded-xl border border-amber-500/25 bg-amber-500/6 p-4">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--amber-text)]">Interne — masqué au client</p>
                  <PrixForces forces={form.prixForces || {}} totals={totals} onChange={(prixForces) => setF({ prixForces })} />
                </div>
              )}

              {/* Éligibilité */}
              {totals.eligible ? (
                <div data-testid="calc-eligibility-ok" className="flex items-start gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/8 px-4 py-3 text-[12px] leading-relaxed text-[var(--green-text)]">
                  <BadgeCheck size={15} className="mt-0.5 shrink-0" />
                  Conditions d'éligibilité réunies pour l'ensemble des {ops.length} opération(s) de ce dossier (sous réserve des autres
                  critères de la fiche applicable : réalisation par un professionnel, justificatifs requis, etc.).
                </div>
              ) : (
                <div data-testid="calc-eligibility-ko" className="rounded-xl border border-amber-500/30 bg-amber-500/8 px-4 py-3 text-[12px] leading-relaxed text-[var(--amber-text)]">
                  <p className="mb-1 flex items-center gap-2 font-semibold"><AlertTriangle size={14} /> Points de vigilance</p>
                  <ul className="list-inside list-disc space-y-0.5 text-[var(--amber-text)] opacity-90">
                    {totals.motifs.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2.5 pt-1">
                <button data-testid="calc-save-dossier-btn" onClick={saveDossier} disabled={saving} className="btn-primary w-full py-3">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {!isAdmin ? "Calculer la prime" : editId ? "Mettre à jour le dossier" : "Enregistrer le dossier"}
                </button>
                <div className="grid grid-cols-2 gap-2.5">
                  <button data-testid="calc-devis-btn" onClick={generateDevis} className="btn-ghost py-2.5">
                    <FileText size={14} /> Générer l'estimation
                  </button>
                  <button data-testid="calc-add-sim-btn" onClick={addSimulation} className="btn-ghost py-2.5">
                    <GitCompareArrows size={14} /> Comparer
                  </button>
                </div>
                {editId && (
                  <button data-testid="calc-reset-btn" onClick={resetAll} className="btn-ghost w-full py-2.5 text-[var(--dim)]">
                    <RotateCcw size={14} /> Réinitialiser la saisie
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {devis && <DevisDocument dossier={devis} onClose={() => setDevis(null)} />}
    </div>
  );
};

export default Calculateur;
